import React from 'react';
import { Button } from './ui/button';

const ShowSection = () => {
  const highlights = [
    {
      title: "Grand Scale Illusions",
      description: "Witness impossible feats that defy logic and leave audiences speechless",
      icon: "✨"
    },
    {
      title: "Mind-Bending Mentalism",
      description: "Experience the power of the mind with stunning displays of telepathy",
      icon: "🧠"
    },
    {
      title: "Interactive Magic",
      description: "Become part of the show with audience participation moments",
      icon: "🎭"
    },
    {
      title: "Comedic Performance",
      description: "Laugh along with perfectly timed humor and entertaining storytelling",
      icon: "😄"
    }
  ];

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section id="show" className="py-20 bg-gradient-to-br from-purple-900 via-black to-purple-900">
      <div className="container mx-auto px-4">
        <div className="max-w-6xl mx-auto">
          {/* Header */}
          <div className="text-center mb-16 relative">
            {/* Playing Now Stamp */}
            <div className="absolute top-0 right-4 md:right-20 transform rotate-12 z-20">
              <div 
                className="px-6 py-3 border-4 border-green-500 rounded-lg"
                style={{
                  fontFamily: 'Impact, sans-serif',
                  boxShadow: 'inset 0 0 0 2px green',
                }}
              >
                <span 
                  className="text-green-500 text-2xl md:text-3xl font-bold tracking-wider uppercase"
                  style={{
                    textShadow: '1px 1px 0 rgba(0,0,0,0.5)',
                    letterSpacing: '3px',
                  }}
                >
                  Playing Now
                </span>
              </div>
            </div>

            <h2 className="text-4xl md:text-6xl font-bold text-white mb-6">
              <span className="text-purple-400">MENTAL</span> HOP SCOTCH
            </h2>
            <div className="text-lg text-gray-300 max-w-4xl mx-auto leading-relaxed text-left space-y-4">
              <p>
                "Mental Hop Scotch" is a high-octane, 90-minute mental magic experience where the stage is the entire audience and the line between reality and the impossible is completely erased. Performed by the master of psychological illusion, Simon Nomis, this isn't a show you watch—it's a game you play.
              </p>
              <p>
                In this wildly interactive performance, Simon leaps from mind to mind in a breathtaking display of mental agility. He doesn't just guess thoughts; he plucks them from the air, influences choices, and reveals information that simply couldn't be known. The show is a series of escalating, jaw-dropping moments where audience members don't just participate—they become the stars of the show.
              </p>
              <p>
                But the experience doesn't end when the curtain falls. Simon ensures the magic is tangible. Audience members walk away with cash won in impossible predictions, actual props used in the show as souvenirs, and a profound sense of amazement that will have them questioning the very nature of their own minds long after the final applause. "Mental Hop Scotch" is mental magic for the modern age: raw, personal, and unforgettable.
              </p>
            </div>
          </div>

          {/* Show Image */}
          <div className="relative mb-16">
            <div className="aspect-video rounded-lg overflow-hidden shadow-2xl">
              <img
                src="https://customer-assets.emergentagent.com/job_reality-bender/artifacts/faasyh1b_selfieonstage.jpg"
                alt="Simon Nomis performing live on stage with packed audience"
                className="w-full h-full object-cover"
              />
            </div>
            <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent rounded-lg"></div>
            <div className="absolute bottom-4 left-4 text-white">
              <h3 className="text-2xl font-bold mb-2">Sold-Out Performances</h3>
              <p className="text-lg text-gray-300">Captivating audiences worldwide</p>
            </div>
          </div>

          {/* Obscuring Reality - Coming Soon Section */}
          <div className="text-center mb-16 relative mt-20 pt-16 border-t border-purple-500/30">
            {/* Coming Soon Stamp */}
            <div className="absolute top-8 right-4 md:right-20 transform rotate-12 z-20">
              <div 
                className="px-6 py-3 border-4 border-red-500 rounded-lg"
                style={{
                  fontFamily: 'Impact, sans-serif',
                  boxShadow: 'inset 0 0 0 2px red',
                }}
              >
                <span 
                  className="text-red-500 text-2xl md:text-3xl font-bold tracking-wider uppercase"
                  style={{
                    textShadow: '1px 1px 0 rgba(0,0,0,0.5)',
                    letterSpacing: '3px',
                  }}
                >
                  Coming Soon
                </span>
              </div>
            </div>

            <h2 className="text-4xl md:text-6xl font-bold text-white mb-6">
              <span className="text-purple-400">OBSCURING</span> REALITY
            </h2>
            <div className="text-lg text-gray-300 max-w-4xl mx-auto leading-relaxed text-left space-y-4">
              <p>
                Prepare to have your perception of the possible shattered. "Obscuring Reality" is a breathtaking, two-hour spectacle of grand illusion coming to Orlando in July 2026, bringing the mind-bending power of a Las Vegas mega-show to the heart of the city.
              </p>
              <p>
                Performed by the master illusionist Simon, this is an immersive journey into a world where the laws of physics are merely suggestions. Witness impossible feats of magic unfold on a grand scale: from stunning levitations and visual magic that defies explanation to death-defying escapes and appearances that will leave you gasping. This is not a show; it is a live event designed to push the boundaries of imagination and blur the line between what you see and what is real.
              </p>
              <p>
                The magic is already being built. Get a sneak peek behind the curtain and witness the spectacle as it comes together by visiting our Gallery. See the massive props being constructed and the stage being set for an experience that will redefine magic in Orlando. "Obscuring Reality"—where seeing is not believing.
              </p>
            </div>
          </div>

          {/* Highlights Grid */}
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 mb-16">
            {highlights.map((highlight) => (
              <div key={highlight.title} className="text-center">
                <div className="text-4xl mb-4">{highlight.icon}</div>
                <h3 className="text-xl font-bold text-white mb-3">{highlight.title}</h3>
                <p className="text-gray-400 leading-relaxed">{highlight.description}</p>
              </div>
            ))}
          </div>

          {/* Call to Action */}
          <div className="text-center">
            <div className="bg-black/50 backdrop-blur-sm rounded-lg p-8 border border-purple-500/20">
              <h3 className="text-3xl font-bold text-white mb-4">
                Experience the Impossible
              </h3>
              <p className="text-lg text-gray-300 mb-6 max-w-2xl mx-auto">
                Join us for an evening of wonder, mystery, and entertainment that will leave you questioning the boundaries of reality.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button
                  onClick={() => scrollToSection('tickets')}
                  className="bg-purple-600 hover:bg-purple-700 text-white px-8 py-4 text-lg rounded-full transition-all duration-300 transform hover:scale-105 hover:shadow-lg"
                >
                  Reserve Your Seats
                </Button>
                <Button
                  variant="outline"
                  className="border-purple-400 text-purple-400 hover:bg-purple-400 hover:text-black px-8 py-4 text-lg rounded-full transition-all duration-300 transform hover:scale-105"
                >
                  View Show Schedule
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ShowSection;